/**
 * PuzzleRoyaleTournamentScreen (P8)
 *
 * The durable, state-aware DEEP-LINK target for a single Puzzle Royale
 * tournament: web route `puzzle-royale-tournament/:tournamentId`. It is the
 * ONE public link for a tournament across its whole lifecycle — the detail
 * component below decides what to show from the tournament's own state
 * (scheduled → register, live → join/watch, completed → results, cancelled →
 * notice). This is why the link is durable and id-keyed: unlike the ephemeral
 * join `share_code` (which only works while live), the numeric id resolves at
 * any time.
 *
 * IMPORTANT: this is a SEPARATE route from the chess-tournament flow
 * (`CCTournaments?tournamentId=`) — the id spaces are different and must not be
 * crossed (skill.md pitfall 34).
 *
 * The screen is a thin wrapper: it pulls `tournamentId` off the route params
 * (deep-link path param OR navigate() param) and renders
 * PuzzleRoyaleTournamentDetail, which owns all the state-aware behaviour and is
 * also embedded inline by the discovery tab. Back goes to the tournaments page.
 */

import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useRoute} from '@react-navigation/native';

import CCColors from '@/Utils/CCColors';
import PuzzleRoyaleTournamentDetail from './components/PuzzleRoyaleTournamentDetail';

export default function PuzzleRoyaleTournamentScreen() {
  const route = useRoute<any>();
  // `royaleTournamentId` is the CANONICAL param. It is deliberately a DIFFERENT
  // NAME from the chess `tournamentId`: react-navigation merges params across a
  // navigate(), so while both routes used `tournamentId` this screen's ROYALE id
  // rode along to PlayChess (the CCTournaments screen), whose
  // `if (tournamentId) getTournamentDetails(tournamentId)` opened the CHESS
  // tournament of the SAME NUMBER — e.g. royale 40 opened chess tournament 40
  // (skill.md pitfall 34). `tournamentId`/`id` are still read as fallbacks so
  // links minted before the rename keep resolving.
  const tournamentId =
    route?.params?.royaleTournamentId ??
    route?.params?.tournamentId ??
    route?.params?.id ??
    null;

  // No Back affordance. The left nav is always visible so this screen never
  // needed one, and every routing attempt landed somewhere wrong.

  return (
    <View style={styles.root}>
      <View style={styles.centerColumn}>
        {tournamentId != null ? (
          <PuzzleRoyaleTournamentDetail tournamentId={tournamentId} />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {flex: 1, minHeight: 0, backgroundColor: '#FAF6EB'},
  centerColumn: {
    flex: 1,
    // minHeight:0 so the shell below can be bounded and its rail scrolls
    // internally instead of inflating this column (see RoyaleLobbyShell).
    minHeight: 0,
    width: '100%',
    // NO maxWidth and NO padding. PuzzleRoyaleBattle renders edge-to-edge and
    // RoyaleLobbyShell supplies its own padding:20, so capping this at 1040px
    // (plus 16px of extra padding) squeezed the same shell into a much narrower
    // box — which is why the tournament's right rail looked tiny next to the
    // live screen's. Removing the cap makes the two genuinely identical.
    alignSelf: 'center',
  },
});
